import React, { useEffect } from 'react';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { connect } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { Socket } from '../../socketProvider';

import LayoutNavbar from './LayoutNavbar';
import LayoutSidenav from './LayoutSidenav';
import GlobalSocketEvents from './GlobalSocketEvents';
import layoutHelpers from './helpers';
import Alert from '../../components/Alert';
import LightBox from '../../components/Snapshot/LightBox';
import PromotionModal from '../../components/Promotion/Modal';
import Users from '../../pages/Admin/Users';
import Roles from '../../pages/Admin/Roles';
import Calendar from '../../pages/Calendar';
import Album from '../../pages/Album';
import ShareOptions from '../../pages/Template/ShareOptions';
import Geofence from '../../pages/Template/Geofence';
import Languages from '../../pages/Template/Languages';
import Graphics from '../../pages/Template/Graphics';
import EventUsers from '../../pages/EventUsers';
import Monitor from '../../pages/Monitor';
import Snapshot from '../../pages/Snapshot';
import Analytics from '../../pages/Analytics';
import CrowdCount from '../../pages/CrowdCount';
import CameraConfigs from '../../pages/Config/CameraConfigs';
import MappingInfo from '../../pages/Config/MappingInfo';
import Colors from '../../pages/Template/Colors';
import Trivia from '../../pages/Template/Trivia';
import Overlays from '../../pages/Template/Overlays';
import SeatOptions from '../../pages/Template/SeatOptions';
import Export from '../../pages/Template/Export';
import Slideshow from '../../pages/Template/Slideshow';
import AiConcept from '../../pages/AiTemplate/Concept';
import Mapping from '../../pages/Mapping';
import KeyListener from '../../components/KeyListener';
import NotFound from '../NotFound';
import Authorize from '../../components/Authorize';

import { getEventTemplate } from '../../helpers/api';
import { setEventTemplate } from '../../store/actions/events';
import {
  ANALYTICS_READ,
  MONITOR_READ,
  USERS,
  CONFIG,
  ROLES_READ,
  EVENT_USERS_READ,
  TRIVIA,
  GRAPHICS,
  OVERLAYS,
  SLIDESHOW,
  TEMPLATE,
} from '../../components/Authorize/permissions/resources';

import { urls } from '../../helpers/urls';
import { fetchRoles } from '../../pages/Admin/Roles/actions';
import env from '../../config/env';

const { websocket } = urls;

const noPermissionFallback = (
  <div className="d-flex flex-grow-1 justify-content-center align-items-center">
    <h5>Insufficient permissions</h5>
  </div>
);

const withAuthorization = (element, permissions, or = false) => (
  <Authorize permissions={permissions} or={or} fallback={noPermissionFallback}>
    {element}
  </Authorize>
);

const buildRoutes = (isSelfieCam) => {
  const routes = [
    { path: '/', element: <Calendar /> },
    { path: '/admin/users', element: withAuthorization(<Users />, USERS) },
    { path: '/admin/roles', element: withAuthorization(<Roles />, ROLES_READ) },
    { path: '/album/', element: <Album /> },
    { path: '/template/colors', element: withAuthorization(<Colors />, TEMPLATE) },
    { path: '/template/Languages', element: withAuthorization(<Languages />, TEMPLATE) },
    { path: '/template/share', element: withAuthorization(<ShareOptions />, TEMPLATE) },
    { path: '/template/graphics', element: withAuthorization(<Graphics />, [GRAPHICS, TEMPLATE], true) },
    { path: '/template/export', element: <Export /> },
    { path: '/eventUsers/', element: withAuthorization(<EventUsers />, EVENT_USERS_READ) },
    { path: '/monitor/', element: withAuthorization(<Monitor />, MONITOR_READ) },
    { path: '/snapshot/:snapshotId', element: <Snapshot /> },
    { path: '/analytics', element: withAuthorization(<Analytics />, ANALYTICS_READ) },
    { path: '/analytics/:eventId', element: withAuthorization(<Analytics />, ANALYTICS_READ) },
    { path: '*', element: <NotFound /> },
  ];

  if (isSelfieCam) {
    routes.push(
      {
        path: '/template/ai-concept',
        element: withAuthorization(<AiConcept />, TEMPLATE),
      },
      {
        path: '/template/slideshow',
        element: withAuthorization(<Slideshow />, [SLIDESHOW, TEMPLATE], true),
      },
    );
    return routes;
  }

  routes.push(
    { path: '/crowd-count', element: withAuthorization(<CrowdCount />, ANALYTICS_READ) },
    { path: '/crowd-count/:eventId', element: withAuthorization(<CrowdCount />, ANALYTICS_READ) },
    { path: '/template/geofence', element: withAuthorization(<Geofence />, TEMPLATE) },
    { path: '/template/trivia', element: withAuthorization(<Trivia />, [TRIVIA, TEMPLATE], true) },
    { path: '/template/overlays', element: withAuthorization(<Overlays />, [OVERLAYS, TEMPLATE], true) },
    { path: '/template/seatOptions', element: withAuthorization(<SeatOptions />, TEMPLATE) },
    { path: '/template/slideshow', element: withAuthorization(<Slideshow />, [SLIDESHOW, TEMPLATE], true) },
    { path: '/config/camera/', element: withAuthorization(<CameraConfigs />, CONFIG) },
    { path: '/config/mapping/', element: withAuthorization(<MappingInfo />, CONFIG) },
    { path: '/mapping', element: withAuthorization(<Mapping />, CONFIG) },
  );

  return routes;
};

const Layout = (props) => {
  const {
    isUserLogged,
    isSessionChecked,
    setEventTemplateAction,
    fetchRolesAction,
  } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const routes = buildRoutes(env.isSelfieCam());

  useEffect(() => {
    layoutHelpers.init();
    layoutHelpers.update();
    layoutHelpers.setAutoUpdate(true);
    layoutHelpers.setCollapsed(true);

    return () => {
      layoutHelpers.destroy();
    };
  }, []);

  useEffect(() => {
    if (isUserLogged) {
      fetchRolesAction();
    }
  }, [isUserLogged]);

  useEffect(() => {
    if (isSessionChecked && !isUserLogged) {
      navigate('/login');
    }
  }, [isUserLogged, isSessionChecked, navigate]);

  useEffect(() => {
    if (!isUserLogged) {
      return;
    }

    // TODO: refactor - move most of the logic to reducer
    const loadTemplateEvent = async () => {
      try {
        const template = await getEventTemplate();
        setEventTemplateAction(template);
      } catch (err) {
        if (err.statusCode !== 404) {
          console.error(err);

          await Alert.fire({
            title: 'Something went wrong',
            html: 'Please click OK to load template again',
            allowOutsideClick: false,
          });

          loadTemplateEvent();
          return;
        }

        // since the API create this on init, this should never happen
        if (err.statusCode === 404) {
          await Alert.fire({
            title: "Template event doesn't exist",
            html: 'Please notify the support team.',
            allowOutsideClick: false,
          });

          navigate('/login');
        }
      }
    };

    loadTemplateEvent();
  }, [setEventTemplateAction, navigate, isUserLogged]);

  const closeSidenav = (e) => {
    e.preventDefault();
    layoutHelpers.setCollapsed(true);
  };

  // Don't render protected routes until session is checked to avoid 401 errors
  if (!isSessionChecked || !isUserLogged) {
    return null;
  }

  return (
    <Socket
      uri={websocket}
      options={{
        transports: ['websocket'],
        auth: { token: env.BRIZI_APP_SLUG },
        query: { source: 'dashboard' },
      }}
    >
      <div className="layout-wrapper layout-1">
        <KeyListener />
        <GlobalSocketEvents />
        <div className="layout-inner">
          <LayoutNavbar />

          <div className="layout-container">
            <LayoutSidenav location={location} />

            <div className="layout-content">
              <div className="flex-grow-1 d-flex flex-column">
                <Routes>
                  {routes.map((route) => (
                    <Route key={route.path} path={route.path} element={route.element} />
                  ))}
                </Routes>
              </div>
            </div>
          </div>
        </div>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div className="layout-overlay" onClick={closeSidenav} onKeyDown={closeSidenav} />

        <LightBox />
        <PromotionModal />
        <ToastContainer enableMultiContainer containerId="suspicious" position="bottom-right" autoClose={5000} />
      </div>
    </Socket>
  );
};

export default connect(
  ({ authentication }) => ({
    isUserLogged: authentication.logged,
    isSessionChecked: authentication.sessionChecked,
  }),
  {
    setEventTemplateAction: setEventTemplate,
    fetchRolesAction: fetchRoles,
  },
)(Layout);
